import React from 'react';
import '../ingestion-filtering.css';

const IngestionFiltering = () => {
  return (
    <div className="ingestion-filtering-page">
      <div className="if-page-inner">
        {/* Top bar */}
        <div className="if-top-bar">
          <div className="if-brand">
            
            <div>
              <div className="if-brand-text-primary">Pre‑Ingestion Filtering</div>
              <div className="if-brand-text-secondary">Stream‑native, cost‑optimised security telemetry</div>
            </div>
          </div>
          <div className="if-top-badge">
            <span className="if-pill-dot"></span>
            <span>MITRE ATT&amp;CK aligned · Confluent‑ready architecture</span>
          </div>
        </div>

        {/* Hero */}
        <section className="if-hero">
          <div className="if-hero-text">
            <h1>
              Cut SIEM ingest by
              <span className="if-hero-gradient"> 60–70% </span>
              while keeping every critical signal.
            </h1>
            <p className="if-hero-subtitle">
              A visually layered pre‑ingestion filtering blueprint for Splunk and modern SIEMs, built
              on Confluent streams, that removes noise and duplicates while preserving MITRE
              ATT&amp;CK‑aligned detection coverage.
            </p>
            <div className="if-hero-metadata">
              <div className="if-meta-pill">
                <strong>4‑Path</strong> stream architecture
              </div>
              <div className="if-meta-pill">
                <strong>10</strong> filtering layers
              </div>
              <div className="if-meta-pill">
                <strong>AI</strong> aggregation &amp; ksqlDB baselining
              </div>
            </div>
          </div>

          <div className="if-hero-visual-wrap">
            <div className="if-hero-visual">
              <div className="if-pipeline">
                <div className="if-pipeline-header">
                  <div className="if-pipeline-title">Pre‑Ingestion Flow</div>
                  <div className="if-pipeline-tag">
                    <span className="if-pipeline-tag-dot"></span>
                    Real‑time EPS optimisation
                  </div>
                </div>
                <div className="if-pipeline-row">
                  <div className="if-pipeline-step">
                    <div className="if-pipeline-step-label">
                      <span>1</span> Path
                    </div>
                    <div className="if-pipeline-step-title">Raw Log Streams</div>
                    <div>All events from network, endpoints, apps, cloud &amp; tools.</div>
                    <div className="if-pipeline-metrics">
                      <span className="if-metric-chip">100% EPS</span>
                    </div>
                  </div>
                  <div className="if-pipeline-step">
                    <div className="if-pipeline-step-label">
                      <span>2</span> Path
                    </div>
                    <div className="if-pipeline-step-title">Intelligent Filters</div>
                    <div>Deduplication, noise removal, sampling &amp; protocol rules.</div>
                    <div className="if-pipeline-metrics">
                      <span className="if-metric-chip"><strong>30–40%</strong> Basic</span>
                      <span className="if-metric-chip"><strong>20–30%</strong> Rules</span>
                    </div>
                  </div>
                  <div className="if-pipeline-step">
                    <div className="if-pipeline-step-label">
                      <span>3</span> Path
                    </div>
                    <div className="if-pipeline-step-title">AI Aggregation</div>
                    <div>Statistical baselines, ML &amp; context‑rich incidents.</div>
                    <div className="if-pipeline-metrics">
                      <span className="if-metric-chip"><strong>25–35%</strong> ML</span>
                      <span className="if-metric-chip"><strong>40–60%</strong> AI</span>
                    </div>
                  </div>
                  <div className="if-pipeline-step">
                    <div className="if-pipeline-step-label">
                      <span>4</span> Path
                    </div>
                    <div className="if-pipeline-step-title">Cold / Compliance</div>
                    <div>Full‑fidelity archives for PCI, GDPR &amp; audit‑only needs.</div>
                    <div className="if-pipeline-metrics">
                      <span className="if-metric-chip">SIEM sees security‑relevant subset only</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* KPI cards */}
        <section className="if-kpi-section">
          <h2 className="if-section-title">Quantified Benefits</h2>
          <p className="if-section-subtitle">
            Layer basic deduplication, intelligent rules, ML baselines and AI aggregation to show
            progressive, measurable EPS &amp; cost reduction — without sacrificing security outcomes.
          </p>
          <div className="if-kpi-grid">
            <div className="if-kpi-card">
              <div className="if-kpi-label">Filter Type</div>
              <div className="if-kpi-name">Basic Dedup &amp; Noise</div>
              <div className="if-kpi-number">30–40%</div>
              <div className="if-kpi-helper">Volume reduction</div>
              <div className="if-kpi-helper">Cost savings: 25–35%</div>
              <div className="if-effort-pill">
                <span className="if-effort-dot if-effort-low"></span> Effort: Low
              </div>
            </div>
            <div className="if-kpi-card">
              <div className="if-kpi-label">Filter Type</div>
              <div className="if-kpi-name">Intelligent Rules</div>
              <div className="if-kpi-number">20–30%</div>
              <div className="if-kpi-helper">Incremental volume reduction</div>
              <div className="if-kpi-helper">Cost savings: 20–25%</div>
              <div className="if-effort-pill">
                <span className="if-effort-dot if-effort-med"></span> Effort: Medium
              </div>
            </div>
            <div className="if-kpi-card">
              <div className="if-kpi-label">Filter Type</div>
              <div className="if-kpi-name">Statistical / ML</div>
              <div className="if-kpi-number">25–35%</div>
              <div className="if-kpi-helper">Baseline‑driven reduction</div>
              <div className="if-kpi-helper">Cost savings: 30–40%</div>
              <div className="if-effort-pill">
                <span className="if-effort-dot if-effort-high"></span> Effort: High
              </div>
            </div>
            <div className="if-kpi-card">
              <div className="if-kpi-label">Filter Type</div>
              <div className="if-kpi-name">AI Aggregation</div>
              <div className="if-kpi-number">40–60%</div>
              <div className="if-kpi-helper">Events → enriched incidents</div>
              <div className="if-kpi-helper">Cost savings: 50–70%</div>
              <div className="if-effort-pill">
                <span className="if-effort-dot if-effort-high"></span> Effort: High
              </div>
            </div>
          </div>
        </section>

        {/* Filtering layers */}
        <section className="if-filters-section">
          <h2 className="if-section-title">Filtering Layers</h2>
          <p className="if-section-subtitle">
            Ten complementary layers applied at the stream level. Each layer targets high‑volume
            patterns while ensuring no loss of critical telemetry for detection and response.
          </p>

          <div className="if-filters-grid">
            {filterLayers.map((layer, index) => (
              <article key={index} className="if-filter-card">
                <div className="if-filter-header">
                  <div>
                    <div className="if-filter-id">{layer.id}</div>
                    <div className="if-filter-name">{layer.name}</div>
                  </div>
                  <div className="if-filter-impact">
                    Impact: <strong>{layer.impact}</strong>
                  </div>
                </div>
                <div className="if-filter-body">
                  <strong>{layer.whatLabel}</strong>
                  <ul className="if-filter-list">
                    {layer.whatItems.map((item, i) => (
                      <li key={i}>{item}</li>
                    ))}
                  </ul>
                  {layer.implLabel && (
                    <>
                      <strong>{layer.implLabel}</strong>
                      <ul className="if-filter-list">
                        {layer.implItems.map((item, i) => (
                          <li key={i} dangerouslySetInnerHTML={{ __html: item }}></li>
                        ))}
                      </ul>
                    </>
                  )}
                  <div className="if-filter-meta-row">
                    <span className="if-filter-meta-pill">
                      <strong>Why safe:</strong> {layer.whySafe}
                    </span>
                    <span className="if-filter-meta-pill">
                      <strong>MITRE:</strong> {layer.mitre}
                    </span>
                  </div>
                </div>
              </article>
            ))}
          </div>
        </section>

        {/* Roadmap */}
        <section className="if-roadmap">
          <h2 className="if-section-title">Implementation Priority</h2>
          <p className="if-section-subtitle">
            Start with quick wins to prove value in days, then progress to intelligent rules,
            statistical baselines and AI‑driven aggregation to unlock full savings.
          </p>
          <div className="if-roadmap-grid">
            <div className="if-roadmap-lane">
              <div className="if-lane-label">0–2 Weeks</div>
              <div className="if-lane-title">Quick Wins</div>
              <ul className="if-lane-list">
                <li>Duplicate elimination across major network &amp; endpoint sources.</li>
                <li>Noise filtering for known‑good DNS, health checks &amp; successful auth.</li>
                <li>Basic sampling on low‑value allow traffic &amp; HTTP 200/304 responses.</li>
              </ul>
            </div>
            <div className="if-roadmap-lane">
              <div className="if-lane-label">1–2 Months</div>
              <div className="if-lane-title">Medium Term</div>
              <ul className="if-lane-list">
                <li>Asset criticality tiers (Tier 1–4) mapped to filtering policies.</li>
                <li>Time‑based filtering based on business hours &amp; risk windows.</li>
                <li>Compliance‑driven routing to cold storage for PCI / GDPR.</li>
              </ul>
            </div>
            <div className="if-roadmap-lane">
              <div className="if-lane-label">3+ Months</div>
              <div className="if-lane-title">Advanced</div>
              <ul className="if-lane-list">
                <li>Statistical baselines with Confluent ksqlDB.</li>
                <li>AI enrichment &amp; aggregation (events → incidents).</li>
                <li>Dynamic threshold adjustment &amp; continuous tuning.</li>
              </ul>
            </div>
          </div>
        </section>

        {/* Measurement */}
        <section className="if-measurement">
          <h2 className="if-section-title">Measurement Framework</h2>
          <p className="if-section-subtitle">
            Track these metrics to quantify ROI and validate that filtering improves — not degrades —
            your security posture.
          </p>
          <table className="if-metric-table">
            <thead>
              <tr>
                <th>Metric</th>
                <th>Definition</th>
                <th>Objective</th>
              </tr>
            </thead>
            <tbody>
              {measurementMetrics.map((metric, index) => (
                <tr key={index}>
                  <td>{metric.name}</td>
                  <td>{metric.definition}</td>
                  <td>{metric.objective}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>

        {/* Security assurance */}
        <section className="if-security-panel">
          <div className="if-security-header">
            <div>
              <div className="if-security-title">Security Assurance</div>
              <div className="if-security-subtitle">
                Filter noise, not signal. Every layer is mapped to MITRE ATT&amp;CK coverage so you
                retain full visibility across tactics while dramatically reducing volume.
              </div>
            </div>
          </div>
          <div className="if-security-grid">
            <ul className="if-security-list">
              <li>Tier‑1 assets (DCs, PKI, PAM &amp; crown‑jewel apps) ingest at full fidelity.</li>
              <li>Failures, anomalies, privilege changes &amp; policy violations are never filtered.</li>
              <li>
                At least one high‑fidelity data source is maintained for each ATT&amp;CK tactic:
                initial access, execution, persistence, privilege escalation, defense evasion,
                credential access, discovery, lateral movement, collection, exfiltration, C2.
              </li>
              <li>
                Compliance‑required logs are retained in cold storage for full forensic reconstruction.
              </li>
              <li>
                AI aggregation replaces raw noise with enriched incidents, improving analyst context.
              </li>
            </ul>
            <div className="if-security-chip-stack">
              {mitreChips.map((chip, index) => (
                <span key={index} className="if-security-chip">{chip}</span>
              ))}
            </div>
          </div>
        </section>

      </div>
    </div>
  );
};

// Data arrays
const filterLayers = [
  {
    id: 'Layer 01',
    name: 'Duplicate Event Elimination',
    impact: '15–30% reduction',
    whatLabel: 'What to filter',
    whatItems: [
      'Identical events from redundant collectors or clusters.',
      'Repeated status/heartbeat messages with no state change.'
    ],
    implLabel: 'Implementation',
    implItems: [
      'Hash‑based deduplication at stream/partition level.',
      'Time windowing (e.g. 30–60 seconds) for near‑duplicates.'
    ],
    whySafe: 'Removes redundant copies only; first instance is preserved.',
    mitre: 'No ATT&CK coverage loss — all unique events still ingested.'
  },
  {
    id: 'Layer 02',
    name: 'Noise Filtering',
    impact: '20–40% reduction',
    whatLabel: 'What to filter',
    whatItems: [
      'Successful authentication events (keep failures/anomalies).',
      'Routine health checks and heartbeats (sample instead of full stream).',
      'DNS queries to known good / top ranked domains.'
    ],
    implLabel: 'Implementation',
    implItems: [
      'Allow‑lists for known‑good services &amp; domains.',
      'Policy‑driven sampling for health‑check patterns.'
    ],
    whySafe: 'Security value is in failures, anomalies &amp; new domains.',
    mitre: 'Preserves brute force, credential abuse &amp; C2 detection.'
  },
  {
    id: 'Layer 03',
    name: 'Low‑Value Event Sampling',
    impact: '10–25% reduction',
    whatLabel: 'What to filter',
    whatItems: [
      'Firewall allows to RFC1918/internal addresses.',
      'Load balancer health checks &amp; routine 200 OK responses.',
      'Standard HTTP 200 / 304 responses with no security context.'
    ],
    implLabel: 'Implementation',
    implItems: [
      'Sampling at <strong>1–5%</strong> for trend visibility.',
      'Full fidelity only for error, auth, or anomalous responses.'
    ],
    whySafe: 'Normal operations are high‑volume but low‑signal.',
    mitre: 'Retains anomalies used for exploitation &amp; exfiltration.'
  },
  {
    id: 'Layer 04',
    name: 'Time‑Based Filtering',
    impact: '10–15% reduction',
    whatLabel: 'What to apply',
    whatItems: [
      'Business hours vs non‑business hours differentiation.',
      'Full fidelity for critical assets 24/7.',
      'Sampling of non‑critical systems during low‑risk periods.'
    ],
    implLabel: 'Implementation',
    implItems: [
      'Time‑of‑day &amp; day‑of‑week policies per asset tier.',
      'Stricter thresholds after hours &amp; on weekends.'
    ],
    whySafe: 'High‑risk time windows retain full coverage.',
    mitre: 'Maintains visibility into out‑of‑hours lateral movement.'
  },
  {
    id: 'Layer 05',
    name: 'Asset Criticality Filtering',
    impact: 'Tier‑aware reduction',
    whatLabel: 'Criticality tiers',
    whatItems: [
      '<strong>Tier 1 (Critical)</strong>: DCs, PKI, crown‑jewel apps → no filtering.',
      '<strong>Tier 2 (Important)</strong>: Moderate filtering of known‑good activity.',
      '<strong>Tier 3 (Standard)</strong>: Aggressive filtering &amp; sampling.',
      '<strong>Tier 4 (Dev/Test)</strong>: Minimal retention, heavy sampling.'
    ],
    implLabel: null,
    implItems: [],
    whySafe: 'Highest‑value assets retain 100% fidelity.',
    mitre: 'Tier 1 fully covers credential, DC &amp; control‑plane techniques.'
  },
  {
    id: 'Layer 06',
    name: 'Geographic / Network Zone Filtering',
    impact: 'Zone‑aware reduction',
    whatLabel: 'What to filter',
    whatItems: [
      'Internal‑to‑internal trusted zone traffic (with sampling).',
      'East–west traffic in well‑segmented, low‑risk segments.'
    ],
    implLabel: 'What to keep',
    implItems: [
      'Full visibility for DMZ &amp; external‑facing zones.',
      'All cross‑zone or cross‑region connections.'
    ],
    whySafe: 'High‑risk network edges always log at full fidelity.',
    mitre: 'Maintains coverage for ingress/egress &amp; lateral movement.'
  },
  {
    id: 'Layer 07',
    name: 'Statistical Baseline Filtering',
    impact: '30–50% reduction',
    whatLabel: 'What to filter',
    whatItems: [
      'Events falling within normal statistical patterns.',
      'Predictable diurnal usage patterns &amp; loads.'
    ],
    implLabel: 'Implementation',
    implItems: [
      '<strong>Confluent ksqlDB</strong> for real‑time baselining &amp; thresholds.',
      'Dynamic adjustments as behaviour changes.'
    ],
    whySafe: 'Only anomalies and deviations are forwarded to SIEM.',
    mitre: 'Enhances detection of unusual patterns &amp; low‑and‑slow attacks.'
  },
  {
    id: 'Layer 08',
    name: 'Compliance‑Driven Retention',
    impact: 'SIEM offload',
    whatLabel: 'Routing strategy',
    whatItems: [
      'Compliance‑required logs → cold storage (Path 4).',
      'SIEM receives only security‑relevant subset.'
    ],
    implLabel: 'Examples',
    implItems: [
      '<strong>PCI:</strong> Auth/access logs archived for long‑term retention.',
      '<strong>GDPR:</strong> Privacy‑filtered copies routed into SIEM.'
    ],
    whySafe: 'You still retain all logs — just in cheaper storage.',
    mitre: 'Historical investigations can rehydrate from archive as needed.'
  },
  {
    id: 'Layer 09',
    name: 'Protocol‑Specific Filtering',
    impact: 'High protocol‑level reduction',
    whatLabel: 'Per‑protocol rules',
    whatItems: [
      '<strong>DHCP:</strong> Only keep new device assignments; filter lease renewals.',
      '<strong>NetFlow:</strong> Sample established connections; retain rejects &amp; anomalies.',
      '<strong>Windows Event Logs:</strong> Filter information‑level &amp; operational noise.',
      '<strong>Syslog:</strong> Remove debug/info where not security‑relevant.'
    ],
    implLabel: null,
    implItems: [],
    whySafe: 'Focuses on topology change &amp; abnormal flows.',
    mitre: 'Preserves key telemetry for discovery, persistence &amp; exfiltration.'
  },
  {
    id: 'Layer 10',
    name: 'AI‑Enhanced Smart Context Aggregation',
    impact: '70–90% reduction',
    whatLabel: 'What it does',
    whatItems: [
      'Pre‑enriches and aggregates related events into context‑rich incidents.',
      'Converts raw high‑volume noise into a smaller set of high‑value records.'
    ],
    implLabel: 'Example',
    implItems: [
      '<strong>1000</strong> port scan attempts → <strong>1</strong> enriched incident record.'
    ],
    whySafe: 'Context is improved; nothing is lost — just summarised.',
    mitre: 'Retains evidence for reconnaissance, lateral movement &amp; exfiltration.'
  }
];

const measurementMetrics = [
  {
    name: 'Pre‑filter EPS',
    definition: 'Events per second before applying any filtering layers.',
    objective: 'Baseline for volume & cost modelling.'
  },
  {
    name: 'Post‑filter EPS',
    definition: 'Events per second after each layer & overall.',
    objective: 'Show step‑wise reduction and confirm no loss of critical feeds.'
  },
  {
    name: 'SIEM storage consumption',
    definition: 'Daily & monthly storage utilisation in Splunk / SIEM.',
    objective: 'Trend down as filters & AI aggregation are enabled.'
  },
  {
    name: 'License utilisation %',
    definition: 'Vendor licence % for EPS or GB/day ingest.',
    objective: 'Target headroom for growth & burst capacity.'
  },
  {
    name: 'MTTD (Mean Time To Detection)',
    definition: 'Average time from malicious activity to detection.',
    objective: 'Must not degrade; aim to improve.'
  },
  {
    name: 'False positive rate',
    definition: 'Percentage of alerts that are benign.',
    objective: 'Should improve as noise & duplicates are removed.'
  }
];

const mitreChips = [
  'Initial Access',
  'Execution',
  'Persistence',
  'Privilege Escalation',
  'Defense Evasion',
  'Credential Access',
  'Discovery',
  'Lateral Movement',
  'Collection',
  'Exfiltration',
  'Command & Control'
];

export default IngestionFiltering;